import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { FORMAT_ELEMENT_COMMAND, INDENT_CONTENT_COMMAND, OUTDENT_CONTENT_COMMAND } from 'lexical';
import type { LucideIcon } from 'lucide-react';
import { IndentDecrease, IndentIncrease, List, ListChecks, ListOrdered, ListTree, Pilcrow, TextQuote } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useFeatureEnabled } from '../../core/context/FeaturesContext';
import { ALIGNMENTS } from '../../ui/alignments';
import { ToolbarButton, ToolbarGroup } from '../../ui/ToolbarButton';
import { ToolbarMenu, ToolbarMenuItem } from '../../ui/ToolbarMenu';
import { useBlockActions } from './useBlockActions';
import type { BlockType, ToolbarState } from './useToolbarState';

const PARAGRAPH_FORMATS = [
  { value: 'paragraph', labelKey: 'editor.toolbar.format.normal' },
  { value: 'h2', labelKey: 'editor.toolbar.format.heading-2' },
  { value: 'h3', labelKey: 'editor.toolbar.format.heading-3' },
  { value: 'h4', labelKey: 'editor.toolbar.format.heading-4' },
] as const satisfies readonly { value: BlockType; labelKey: string }[];

const NUMBERED_LISTS = [
  { value: 'number', icon: ListOrdered, labelKey: 'editor.toolbar.numbered-list' },
  { value: 'hierarchical', icon: ListTree, labelKey: 'editor.toolbar.hierarchical-list' },
] as const satisfies readonly { value: BlockType; icon: LucideIcon; labelKey: string }[];

const BULLETED_LISTS = [
  { value: 'bullet', icon: List, labelKey: 'editor.toolbar.bulleted-list' },
  { value: 'check', icon: ListChecks, labelKey: 'editor.toolbar.check-list' },
] as const satisfies readonly { value: BlockType; icon: LucideIcon; labelKey: string }[];

type ListOption = (typeof NUMBERED_LISTS | typeof BULLETED_LISTS)[number];

/** List menus, paragraph format, and the "⋮" popup with alignment, indent and quote. */
export const ParagraphGroup = ({ state, disabled }: { state: ToolbarState; disabled: boolean }) => {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('common');
  const { setBlock } = useBlockActions(state.blockType);
  const hierarchical = useFeatureEnabled('listNumbering');
  const numbered = state.blockType === 'number' || state.blockType === 'hierarchical';
  const bulleted = state.blockType === 'bullet' || state.blockType === 'check';

  // Each button offers the styles of its own kind of list
  const listMenu = (options: readonly ListOption[]) => (close: () => void) =>
    options.map(({ value, icon, labelKey }) => (
      <ToolbarMenuItem
        key={value}
        icon={icon}
        label={t(labelKey)}
        active={state.blockType === value}
        onClick={() => {
          setBlock(value);
          close();
        }}
      />
    ));

  return (
    <ToolbarGroup>
      <ToolbarMenu icon={ListOrdered} label={t('editor.toolbar.numbered-list')} active={numbered} disabled={disabled}>
        {listMenu(hierarchical ? NUMBERED_LISTS : NUMBERED_LISTS.filter(({ value }) => value !== 'hierarchical'))}
      </ToolbarMenu>
      <ToolbarMenu icon={List} label={t('editor.toolbar.bulleted-list')} active={bulleted} disabled={disabled}>
        {listMenu(BULLETED_LISTS)}
      </ToolbarMenu>
      <ToolbarMenu icon={Pilcrow} label={t('editor.toolbar.paragraph-format')} disabled={disabled}>
        {(close) =>
          PARAGRAPH_FORMATS.map(({ value, labelKey }) => (
            <ToolbarMenuItem
              key={value}
              label={t(labelKey)}
              active={state.blockType === value}
              onClick={() => {
                setBlock(value);
                close();
              }}
            />
          ))
        }
      </ToolbarMenu>
      <ToolbarMenu
        icon={Pilcrow}
        caret="more"
        layout="row"
        label={t('editor.toolbar.more-paragraph')}
        active={state.blockType === 'quote'}
        disabled={disabled}
      >
        {() => (
          <>
            {ALIGNMENTS.map(({ value, icon, labelKey }) => (
              <ToolbarButton
                key={value}
                icon={icon}
                label={t(labelKey)}
                active={state.alignment === value}
                onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, value)}
              />
            ))}
            <ToolbarButton
              icon={IndentDecrease}
              label={t('editor.toolbar.outdent')}
              onClick={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)}
            />
            <ToolbarButton
              icon={IndentIncrease}
              label={t('editor.toolbar.indent')}
              onClick={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)}
            />
            <ToolbarButton
              icon={TextQuote}
              label={t('editor.toolbar.quote')}
              active={state.blockType === 'quote'}
              onClick={() => setBlock('quote')}
            />
          </>
        )}
      </ToolbarMenu>
    </ToolbarGroup>
  );
};
